import { computed } from 'vue';
import processProxy from '@/env/processProxy';
import {
  lendiAiOriginFromBaseUrl,
  resolveLendiAiBaseUrl,
} from '@/utils/lendiAiBaseUrl';
import {
  LENDI_IFRAME_TRUSTED_ORIGINS_DEMO,
  LENDI_IFRAME_TRUSTED_ORIGINS_PROD,
  LENDI_IFRAME_TRUSTED_ORIGINS_QA,
  parseIframeTrustedOriginPatterns,
} from '@/utils/iframeTrustedOrigins';
import {
  lendiV3OriginFromBaseUrl,
  resolveLendiV3BaseUrl,
} from '@/utils/lendiV3BaseUrl';

export type IframeChildKind = 'crm' | 'ai';

type FincrmIframeEnvironment = 'production' | 'demo' | 'qa';

function resolveIframeEnvironment(): FincrmIframeEnvironment {
  if (typeof window === 'undefined') {
    return 'qa';
  }

  if (window.__ENV__ === 'production' || window.__ENV__ === 'demo') {
    return window.__ENV__;
  }

  return 'qa';
}

function trustedOriginsForEnvironment(env: FincrmIframeEnvironment): string {
  if (env === 'production')
    return LENDI_IFRAME_TRUSTED_ORIGINS_PROD;
  if (env === 'demo')
    return LENDI_IFRAME_TRUSTED_ORIGINS_DEMO;
  return LENDI_IFRAME_TRUSTED_ORIGINS_QA;
}

function resolveChildBaseUrl(kind: IframeChildKind): string {
  const forceDev = processProxy.VUE_APP_ENV === 'DEV';
  if (kind === 'crm') {
    return resolveLendiV3BaseUrl({ forceDev });
  }

  return resolveLendiAiBaseUrl({ forceDev });
}

function resolveChildOrigin(kind: IframeChildKind): string {
  const baseUrl = resolveChildBaseUrl(kind);
  if (kind === 'crm') {
    return lendiV3OriginFromBaseUrl(baseUrl);
  }

  return lendiAiOriginFromBaseUrl(baseUrl);
}

export function useIframeOrigins(kind: IframeChildKind = 'crm') {

  const allowedOriginPatterns = computed(() => {
    const environment = resolveIframeEnvironment();
    return parseIframeTrustedOriginPatterns(
      trustedOriginsForEnvironment(environment),
    );
  });

  const childBaseUrl = computed(() => resolveChildBaseUrl(kind));
  const childOrigin = computed(() => resolveChildOrigin(kind));

  return {
    allowedOriginPatterns,
    childBaseUrl,
    childOrigin,
  };
}
